import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { clean, format, validate } from "rut.js";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { CHILEAN_COMUNAS, CHILEAN_REGIONS } from "~/constants/places";
import { toast } from "~/hooks/use-toast";
import { useAuxiliaryTypes } from "~/hooks/useAuxiliaryTypes";
import { useSalesChannels } from "~/hooks/useSalesChannels";
import { supabase } from "~/integrations/supabase/client";
import { cn } from "~/lib/utils";
import {
  findBSaleClientByRUT,
  syncClientToBSale,
} from "~/services/bsaleClientService";
import { useProfileStore } from "~/store/profileStore";
import { EMAIL_REGEX } from "~/utils/helpers";

interface AddClientDialogProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  onClientAdded?: (client: ClientData) => void;
}

interface ClientFormValues {
  name: string;
  tax_id: string;
  email: string;
  phone_number: string;
  contact_person: string;
  activity: string;
  fiscal_address_name: string;
  fiscal_address_number: string;
  fiscal_comuna: string;
  fiscal_region: string;
  shipping_address_name: string;
  shipping_address_number: string;
  shipping_comuna: string;
  shipping_region: string;
  assigned_seller: string;
  collection_contact_person: string;
  collection_contact_email: string;
  collection_contact_phone: string;
  sales_channel: string;
  auxiliary_type: string;
  zone: string;
}

interface Seller {
  id: string;
  name: string;
  email: string;
}

// Add a proper interface for client data to avoid using 'any'
interface ClientData {
  name: string;
  tax_id: string;
  email: string;
  phone_number: string;
  contact_person: string;
  activity: string;
  fiscal_address_name: string;
  fiscal_address_number: string;
  fiscal_comuna: string;
  fiscal_region: string;
  shipping_address_name?: string;
  shipping_address_number?: string;
  shipping_comuna?: string;
  shipping_region?: string;
  created_by: string;
  assigned_seller: string;
  sales_condition_id?: string | null;
  fiscal_address_id?: string | null;
  collection_contact_person?: string;
  collection_contact_email?: string;
  collection_contact_phone?: string;
  sales_channel?: string;
  auxiliary_type?: string;
  zone?: string;
  id?: string;
}

const AddClientDialog = ({
  isOpen,
  setOpen,
  onClientAdded,
}: AddClientDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [isLoadingSellers, setIsLoadingSellers] = useState(false);
  const [sellerSearchOpen, setSellerSearchOpen] = useState(false);
  const [sellerSearchQuery, setSellerSearchQuery] = useState("");

  const [copyFiscalAddress, setCopyFiscalAddress] = useState(false);
  const {
    id: userId,
    role: userRole,
    name: userName,
    lastname: userLastname,
  } = useProfileStore();

  // Fetch sales channels and auxiliary types
  const { salesChannels, isLoading: isLoadingSalesChannels } =
    useSalesChannels();
  const { auxiliaryTypes, isLoading: isLoadingAuxiliaryTypes } =
    useAuxiliaryTypes();

  // Check if the current user is a seller or superadmin
  const isSeller = userRole === "seller";
  const isSuperAdmin = userRole === "superadmin";

  const form = useForm<ClientFormValues>({
    defaultValues: {
      name: "",
      tax_id: "",
      email: "",
      phone_number: "",
      contact_person: "",
      activity: "",
      fiscal_address_name: "",
      fiscal_address_number: "",
      fiscal_comuna: "",
      fiscal_region: "",
      shipping_address_name: "",
      shipping_address_number: "",
      shipping_comuna: "",
      shipping_region: "",
      assigned_seller: "",
      collection_contact_person: "",
      collection_contact_email: "",
      collection_contact_phone: "",
      sales_channel: "",
      auxiliary_type: "",
      zone: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (isOpen && userId) {
      // Always set the assigned seller to the current user
      form.setValue("assigned_seller", userId);

      // Only fetch sellers if the user is not a seller (admin, superadmin, etc.)
      if (!isSeller) {
        fetchSellers();
      }
    }
  }, [isOpen, userId, form, isSeller]);

  // Copy fiscal address to shipping address when checkbox is checked
  useEffect(() => {
    if (copyFiscalAddress) {
      const fiscalValues = form.getValues();
      form.setValue("shipping_address_name", fiscalValues.fiscal_address_name);
      form.setValue(
        "shipping_address_number",
        fiscalValues.fiscal_address_number
      );
      form.setValue("shipping_region", fiscalValues.fiscal_region);
      form.setValue("shipping_comuna", fiscalValues.fiscal_comuna);
    } else {
      // Clear shipping address fields when unchecked
      form.setValue("shipping_address_name", "");
      form.setValue("shipping_address_number", "");
      form.setValue("shipping_region", "");
      form.setValue("shipping_comuna", "");
    }
  }, [copyFiscalAddress, form]);

  const fetchSellers = async () => {
    if (!isLoadingSellers) {
      setIsLoadingSellers(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, email")
          .eq("role", "seller")
          .eq("is_active", true);

        if (error) {
          throw error;
        }

        setSellers(data || []);
      } catch (error: unknown) {
        console.error("Error fetching sellers:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los vendedores.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingSellers(false);
      }
    }
  };

  const handleRutChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    let value = e.target.value;
    value = clean(value);
    value = value.replace(/[^0-9kK]/g, "");

    if (value.length > 0) {
      const formattedValue = format(value).replace(/\./g, "");
      onChange(formattedValue);
    } else {
      onChange(value);
    }
  };

  const handleNewClient = async (values: ClientFormValues) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar el usuario actual.",
        variant: "destructive",
      });
      return;
    }

    if (values.tax_id && !validate(values.tax_id)) {
      form.setError("tax_id", {
        type: "manual",
        message: "RUT inválido. Ingrese un RUT válido.",
      });
      return;
    }

    const standardizedRut = values.tax_id
      ? format(values.tax_id).replace(/\./g, "")
      : values.tax_id;

    setIsSubmitting(true);

    try {
      // Check if RUT is unique
      const { data: existingClients, error: rutError } = await supabase
        .from("clients")
        .select("id")
        .eq("tax_id", standardizedRut)
        .limit(1);

      if (rutError) throw rutError;
      if (existingClients && existingClients.length > 0) {
        form.setError("tax_id", {
          type: "manual",
          message: "Ya existe un cliente con este RUT.",
        });
        setIsSubmitting(false);
        return;
      }

      // All clients now get the default sales condition TRANSFERENCIA ANTICIPADA
      const clientData: any = {
        name: values.name,
        tax_id: standardizedRut,
        email: values.email,
        phone_number: values.phone_number,
        contact_person: values.contact_person,
        activity: values.activity,
        created_by: userId,
        assigned_seller: isSeller ? userId : values.assigned_seller || userId,
        sales_condition_id: "c7b68ad1-3869-4f0b-b35b-e07a263fd43e", // TRANSFERENCIA ANTICIPADA
        collection_contact_person: values.collection_contact_person || null,
        collection_contact_email: values.collection_contact_email || null,
        collection_contact_phone: values.collection_contact_phone || null,
        sales_channel: values.sales_channel || null,
        auxiliary_type: values.auxiliary_type || null,
        zone: values.zone || null,
      };

      console.log("Inserting client data:", clientData);

      const { data, error } = await supabase
        .from("clients")
        .insert(clientData)
        .select();

      if (error) {
        throw error;
      }

      // Create fiscal address (always required and default)
      if (data && data[0]) {
        const clientId = data[0].id;
        console.log("Creating fiscal address for client:", clientId);

        // Create fiscal address (default address)
        const { data: fiscalAddressData, error: fiscalAddressError } =
          await supabase
            .from("addresses")
            .insert({
              client_id: clientId,
              street: values.fiscal_address_name,
              number: values.fiscal_address_number,
              complement: null,
              city:
                (CHILEAN_COMUNAS || []).find(
                  (c) => c.value === values.fiscal_comuna
                )?.label ||
                values.fiscal_comuna ||
                "Santiago",
              state:
                (CHILEAN_REGIONS || []).find(
                  (r) => r.value === values.fiscal_region
                )?.label || values.fiscal_region,
              comuna: values.fiscal_comuna,
              is_default: true,
            })
            .select("id")
            .single();

        if (fiscalAddressError) {
          console.error("Error creating fiscal address:", fiscalAddressError);
          throw new Error(
            `Error creando dirección fiscal: ${fiscalAddressError.message}`
          );
        }

        // Set the fiscal address ID on the client
        if (fiscalAddressData) {
          const { error: updateClientError } = await supabase
            .from("clients")
            .update({ fiscal_address_id: fiscalAddressData.id })
            .eq("id", clientId);

          if (updateClientError) {
            console.error(
              "Error setting fiscal address ID on client:",
              updateClientError
            );
            throw new Error(
              `Error vinculando dirección fiscal: ${updateClientError.message}`
            );
          }

          console.log(
            "Successfully set fiscal address ID on client:",
            fiscalAddressData.id
          );
        }

        // Create shipping address if provided and different from fiscal
        if (
          values.shipping_address_name &&
          values.shipping_address_number &&
          values.shipping_region &&
          values.shipping_comuna
        ) {
          // Check if shipping address is different from fiscal
          const isDifferent =
            values.shipping_address_name !== values.fiscal_address_name ||
            values.shipping_address_number !== values.fiscal_address_number ||
            values.shipping_region !== values.fiscal_region ||
            values.shipping_comuna !== values.fiscal_comuna;

          if (isDifferent) {
            const { error: shippingAddressError } = await supabase
              .from("addresses")
              .insert({
                client_id: clientId,
                street: values.shipping_address_name,
                number: values.shipping_address_number,
                complement: null,
                city:
                  (CHILEAN_COMUNAS || []).find(
                    (c) => c.value === values.shipping_comuna
                  )?.label ||
                  values.shipping_comuna ||
                  "Santiago",
                state:
                  (CHILEAN_REGIONS || []).find(
                    (r) => r.value === values.shipping_region
                  )?.label || values.shipping_region,
                comuna: values.shipping_comuna,
                is_default: false,
              });

            if (shippingAddressError) {
              console.error(
                "Error creating shipping address:",
                shippingAddressError
              );
              // Don't throw here, client was created successfully
            }
          }
        }
      }

      // Attempt to sync with BSale if client was created with activity
      if (data && data[0] && values.activity && standardizedRut) {
        try {
          console.log("Attempting to sync new client with BSale...");
          const { bsaleClientId, error: findError } =
            await findBSaleClientByRUT(standardizedRut);

          if (findError) {
            console.warn("Could not find BSale client:", findError);
          } else if (bsaleClientId) {
            const { success, error: syncError } = await syncClientToBSale(
              bsaleClientId,
              {
                activity: values.activity,
                company: values.name,
                email: values.email,
                phone: values.phone_number,
              }
            );

            if (success) {
              console.log("New client activity successfully synced with BSale");
            } else {
              console.warn(
                "Failed to sync new client activity with BSale:",
                syncError
              );
            }
          }
        } catch (syncError) {
          console.warn("Error during BSale sync for new client:", syncError);
          // Don't throw - client creation was successful
        }
      }

      form.reset();
      setCopyFiscalAddress(false);
      setOpen(false);

      if (onClientAdded && data) {
        onClientAdded(data[0]);
      }
    } catch (error: unknown) {
      console.log("error", error);
      console.error("Full error details:", error);
      console.log("Form values at error:", values);
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      console.error("Error adding client:", error);
      toast({
        title: "Error",
        description: errorMessage || "No se pudo añadir el cliente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the current user's display name
  const getCurrentSellerName = () => {
    return `${userName || ""} ${userLastname || ""}`.trim() || "Usuario actual";
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="flex w-full items-center justify-center gap-1 rounded-full text-xs sm:gap-2 sm:text-sm lg:whitespace-nowrap xl:max-w-xs">
          <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">Añadir</span>
          <span className="sm:hidden">Nuevo</span>
          <span className="hidden sm:inline">Nuevo</span>
          <span className="hidden sm:inline">Cliente</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="z-50 max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Añadir Nuevo Cliente</DialogTitle>
          <DialogDescription>
            Ingrese la información del cliente a continuación.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleNewClient)}
            className="space-y-4"
          >
            {/* Información Cliente Section */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-800">
                Información Cliente
              </h3>

              <div className="grid grid-cols-1 gap-3">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{
                    required: "El nombre de la empresa es obligatorio",
                    minLength: {
                      value: 2,
                      message: "El nombre debe tener al menos 2 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre de la Empresa *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nombre de la Empresa"
                          className="rounded-xl"
                          {...field}
                          onChange={(e) => {
                            // Only allow letters, numbers, and spaces
                            const filtered = e.target.value.replace(
                              /[^a-zA-Z0-9\s]/g,
                              ""
                            );
                            field.onChange(filtered.toUpperCase());
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-3">
                <FormField
                  control={form.control}
                  name="tax_id"
                  rules={{
                    required: "El RUT es obligatorio",
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>RUT *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="12345678-9"
                          className="rounded-xl"
                          maxLength={10} // Max length for Chilean RUT (8 digits + verification digit + hyphen)
                          {...field}
                          onChange={(e) => handleRutChange(e, field.onChange)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="activity"
                  rules={{
                    required: "La actividad comercial es obligatoria",
                    minLength: {
                      value: 3,
                      message: "La actividad debe tener al menos 3 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Actividad Comercial (Giro) *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ej: Comercio al por mayor de productos alimenticios"
                          className="rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone_number"
                  rules={{
                    required: "El número de teléfono es obligatorio",
                    minLength: {
                      value: 8,
                      message: "El teléfono debe tener al menos 8 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teléfono *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Número de Teléfono"
                          className="rounded-xl"
                          type="tel"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Tipo de Auxiliar - Superadmin only */}
              {isSuperAdmin && (
                <div className="grid grid-cols-1 gap-3">
                  <FormField
                    control={form.control}
                    name="auxiliary_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Auxiliar</FormLabel>
                        <FormControl>
                          <select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: "right 0.5rem center",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "1.5em 1.5em",
                              paddingRight: "2.5rem",
                            }}
                            disabled={isLoadingAuxiliaryTypes}
                          >
                            <option value="">Seleccionar tipo</option>
                            {auxiliaryTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Datos Comerciales Section */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-800">
                Datos Comerciales
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="contact_person"
                  rules={{
                    required: "La persona de contacto es obligatoria",
                    minLength: {
                      value: 2,
                      message: "El nombre debe tener al menos 2 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contacto Comercial *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nombre de la persona de contacto"
                          className="rounded-xl"
                          {...field}
                          onChange={(e) =>
                            field.onChange(e.target.value.toUpperCase())
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone_number"
                  render={() => (
                    <FormItem>
                      <FormLabel>Teléfono Contacto Comercial</FormLabel>
                      <FormControl>
                        <Input
                          value={form.watch("phone_number")}
                          className="rounded-xl bg-gray-50"
                          type="tel"
                          disabled
                          placeholder="Se usa el teléfono principal"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: "El correo electrónico es obligatorio",
                    pattern: {
                      value: EMAIL_REGEX,
                      message: "Ingrese un correo electrónico válido",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mail Contacto Comercial *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="contacto@empresa.com"
                          className="rounded-xl"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Canal de Venta - Superadmin only */}
                {isSuperAdmin ? (
                  <FormField
                    control={form.control}
                    name="sales_channel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Canal de Venta</FormLabel>
                        <FormControl>
                          <select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: "right 0.5rem center",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "1.5em 1.5em",
                              paddingRight: "2.5rem",
                            }}
                            disabled={isLoadingSalesChannels}
                          >
                            <option value="">Seleccionar canal</option>
                            {salesChannels.map((channel) => (
                              <option key={channel.id} value={channel.id}>
                                {channel.name}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Canal de Venta
                    </label>
                    <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                      Solo configurable por superadministradores
                    </div>
                  </div>
                )}
              </div>

              {/* Zona - Superadmin only */}
              {isSuperAdmin && (
                <div className="grid grid-cols-1 gap-3">
                  <FormField
                    control={form.control}
                    name="zone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zona</FormLabel>
                        <FormControl>
                          <select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: "right 0.5rem center",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "1.5em 1.5em",
                              paddingRight: "2.5rem",
                            }}
                          >
                            <option value="">Seleccionar zona</option>
                            <option value="Centro">Centro</option>
                            <option value="Norte">Norte</option>
                            <option value="Sur">Sur</option>
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Datos Cobranza Section */}
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-800">
                Datos Cobranza
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="collection_contact_person"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contacto Cobranza</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nombre del contacto de cobranza"
                          className="rounded-xl"
                          {...field}
                          onChange={(e) =>
                            field.onChange(e.target.value.toUpperCase())
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="collection_contact_phone"
                  rules={{
                    minLength: {
                      value: 8,
                      message: "El teléfono debe tener al menos 8 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teléfono Persona Cobranza</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Teléfono de contacto de cobranza"
                          className="rounded-xl"
                          type="tel"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="collection_contact_email"
                  rules={{
                    pattern: {
                      value: EMAIL_REGEX,
                      message: "Ingrese un correo electrónico válido",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mail Contacto Cobranza</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="cobranza@empresa.com"
                          className="rounded-xl"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Condición de Venta - Superadmin only */}
                {isSuperAdmin ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Condición de Venta
                    </label>
                    <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                      Se asigna automáticamente: TRANSFERENCIA ANTICIPADA
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Condición de Venta
                    </label>
                    <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                      Solo configurable por superadministradores
                    </div>
                  </div>
                )}
              </div>

              {/* Financial Fields - Superadmin only */}
              {isSuperAdmin && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Monto Asegurado
                    </label>
                    <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                      Se configurará después de crear el cliente
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Días de Pago</label>
                    <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-500">
                      Se configurará después de crear el cliente
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Fiscal Address Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Dirección Fiscal *</h3>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="fiscal_address_name"
                  rules={{
                    required: "La dirección fiscal es obligatoria",
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre de la Calle *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nombre de la calle"
                          className="rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fiscal_address_number"
                  rules={{
                    required: "El número de dirección es obligatorio",
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Número"
                          className="rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="fiscal_region"
                  rules={{
                    required: "La región es obligatoria",
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Región *</FormLabel>
                      <FormControl>
                        <select
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            // Reset comuna when region changes
                            form.setValue("fiscal_comuna", "");
                          }}
                          className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: "right 0.5rem center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "1.5em 1.5em",
                            paddingRight: "2.5rem",
                          }}
                        >
                          <option value="">Seleccionar región</option>
                          {(CHILEAN_REGIONS || [])
                            .sort((a, b) => {
                              if (a.value === "metropolitana") return -1;
                              if (b.value === "metropolitana") return 1;
                              return a.label.localeCompare(b.label);
                            })
                            .map((region) => (
                              <option key={region.value} value={region.value}>
                                {region.label}
                              </option>
                            ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fiscal_comuna"
                  rules={{
                    required: "La comuna es obligatoria",
                  }}
                  render={({ field }) => {
                    const fiscalRegion = form.watch("fiscal_region");
                    const availableComunas = (CHILEAN_COMUNAS || []).filter(
                      (c) => c.region === fiscalRegion
                    );

                    return (
                      <FormItem>
                        <FormLabel>Comuna *</FormLabel>
                        <FormControl>
                          <select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            disabled={!fiscalRegion}
                            className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: "right 0.5rem center",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "1.5em 1.5em",
                              paddingRight: "2.5rem",
                            }}
                          >
                            <option value="">
                              {fiscalRegion
                                ? "Seleccionar comuna"
                                : "Seleccione región primero"}
                            </option>
                            {availableComunas
                              .sort((a, b) => a.label.localeCompare(b.label))
                              .map((comuna) => (
                                <option key={comuna.value} value={comuna.value}>
                                  {comuna.label}
                                </option>
                              ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </div>

            {/* Shipping Address Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  Dirección de Despacho (Opcional)
                </h3>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="copyFiscalAddress"
                    checked={copyFiscalAddress}
                    onChange={(e) => setCopyFiscalAddress(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label
                    htmlFor="copyFiscalAddress"
                    className="cursor-pointer text-sm text-gray-700"
                  >
                    Usar dirección fiscal
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="shipping_address_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre de la Calle</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            copyFiscalAddress
                              ? "Se usará dirección fiscal"
                              : "Nombre de la calle"
                          }
                          className="rounded-xl"
                          disabled={copyFiscalAddress}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="shipping_address_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            copyFiscalAddress
                              ? "Se usará dirección fiscal"
                              : "Número"
                          }
                          className="rounded-xl"
                          disabled={copyFiscalAddress}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="shipping_region"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Región</FormLabel>
                      <FormControl>
                        <select
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            // Reset comuna when region changes
                            form.setValue("shipping_comuna", "");
                          }}
                          disabled={copyFiscalAddress}
                          className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: "right 0.5rem center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "1.5em 1.5em",
                            paddingRight: "2.5rem",
                          }}
                        >
                          <option value="">
                            {copyFiscalAddress
                              ? "Se usará dirección fiscal"
                              : "Seleccionar región"}
                          </option>
                          {(CHILEAN_REGIONS || [])
                            .sort((a, b) => {
                              if (a.value === "metropolitana") return -1;
                              if (b.value === "metropolitana") return 1;
                              return a.label.localeCompare(b.label);
                            })
                            .map((region) => (
                              <option key={region.value} value={region.value}>
                                {region.label}
                              </option>
                            ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="shipping_comuna"
                  render={({ field }) => {
                    const shippingRegion = form.watch("shipping_region");
                    const availableComunas = (CHILEAN_COMUNAS || []).filter(
                      (c) => c.region === shippingRegion
                    );

                    return (
                      <FormItem>
                        <FormLabel>Comuna</FormLabel>
                        <FormControl>
                          <select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            disabled={!shippingRegion || copyFiscalAddress}
                            className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: "right 0.5rem center",
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "1.5em 1.5em",
                              paddingRight: "2.5rem",
                            }}
                          >
                            <option value="">
                              {copyFiscalAddress
                                ? "Se usará dirección fiscal"
                                : shippingRegion
                                  ? "Seleccionar comuna"
                                  : "Seleccione región primero"}
                            </option>
                            {availableComunas
                              .sort((a, b) => a.label.localeCompare(b.label))
                              .map((comuna) => (
                                <option key={comuna.value} value={comuna.value}>
                                  {comuna.label}
                                </option>
                              ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {/* Only show the assigned seller field if NOT a seller */}
              {!isSeller && (
                <FormField
                  control={form.control}
                  name="assigned_seller"
                  rules={{
                    required: "Debe seleccionar un vendedor",
                  }}
                  render={({ field }) => {
                    const selectedSeller = sellers.find(
                      (s) => s.id === field.value
                    );
                    const filteredSellers = sellers.filter((seller) => {
                      const searchTerm = sellerSearchQuery.toLowerCase();
                      const name = (seller.name || "").toLowerCase();
                      const email = (seller.email || "").toLowerCase();
                      const id = (seller.id || "").toLowerCase();

                      return (
                        name.includes(searchTerm) ||
                        email.includes(searchTerm) ||
                        id.includes(searchTerm)
                      );
                    });

                    return (
                      <FormItem>
                        <FormLabel>Vendedor Asignado *</FormLabel>
                        <FormControl>
                          <Popover
                            open={sellerSearchOpen}
                            onOpenChange={setSellerSearchOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={sellerSearchOpen}
                                className="w-full justify-between rounded-xl"
                                type="button"
                              >
                                {selectedSeller
                                  ? selectedSeller.name ||
                                    selectedSeller.email ||
                                    selectedSeller.id
                                  : "Seleccionar vendedor"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-full p-0"
                              align="start"
                              style={{ pointerEvents: "auto" }}
                              onOpenAutoFocus={(e) => e.preventDefault()}
                            >
                              <Command
                                shouldFilter={false}
                                style={{ pointerEvents: "auto" }}
                              >
                                <CommandInput
                                  placeholder="Buscar vendedor..."
                                  value={sellerSearchQuery}
                                  onValueChange={setSellerSearchQuery}
                                />
                                <CommandList>
                                  <CommandEmpty>
                                    {isLoadingSellers ? (
                                      <div className="flex items-center justify-center py-2">
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        <span>Cargando vendedores...</span>
                                      </div>
                                    ) : (
                                      "No se encontraron vendedores"
                                    )}
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {filteredSellers.map((seller) => (
                                      <CommandItem
                                        key={seller.id}
                                        value={seller.id}
                                        onSelect={(currentValue) => {
                                          field.onChange(
                                            currentValue === field.value
                                              ? ""
                                              : currentValue
                                          );
                                          setSellerSearchOpen(false);
                                          setSellerSearchQuery("");
                                        }}
                                        style={{ pointerEvents: "auto" }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            field.value === seller.id
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {seller.name ||
                                          seller.email ||
                                          seller.id}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                className="rounded-full px-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar Cliente"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientDialog;
